import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { NetworkNextStep } from "@/components/NetworkNextStep";
import { motion, type Variants } from "framer-motion";
import { breadcrumbSchema, faqSchema, personSchema, webPageSchema, PRESS_FEATURES } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";
const headshot = "/img/headshot.webp";
const speakingPhoto = "/img/speaking-photo.webp";

const COAST_SRC = "/landing/about-jacksonville-coast.webp";
const COAST_ALT =
  "A Jacksonville Florida coastal marsh at golden hour, with calm tidal water and warm light, anchoring Dr. Refai's home base.";

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function About() {
  const SEO_TITLE =
    "About Shariq Refai, MD, MBA, Psychiatrist and Author";
  const SEO_DESCRIPTION =
    "Meet Shariq Refai, MD, MBA, a board-certified psychiatrist, shrinkMD founder, and author. His training, his background, and how he writes about the mind.";

  const aboutPage = webPageSchema({
    path: "/about",
    title: SEO_TITLE,
    description: SEO_DESCRIPTION,
    image: "/og-about.jpg",
    type: "AboutPage",
    speakableSelectors: [".at-a-glance"],
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ],
  });

  const aboutFaq = faqSchema([
    {
      question: "Where did Shariq Refai go to medical school?",
      answer:
        "Dr. Refai earned his MD from St. George's University School of Medicine. He completed psychiatry residency at the University of Hawaii and at John Peter Smith Hospital in Texas. He earned an MBA from Duke University and certificates in Electroconvulsive Therapy and Obesity Medicine from Columbia University.",
    },
    {
      question: "What are Shariq Refai's areas of expertise?",
      answer:
        "Dr. Refai is dually board-certified in psychiatry and sports/performance psychiatry. His areas of professional expertise include anxiety, depression, OCD, panic, PTSD, bipolar disorder, and addiction psychiatry, with specialized experience in high-performance populations including professional athletes and executives. shariqrefai.com is an educational author and media platform and does not provide clinical care.",
    },
    {
      question: "Where does Shariq Refai practice?",
      answer:
        "Dr. Refai is based in Jacksonville, Florida. He's the founder and CEO of shrinkMD, a multistate telepsychiatry practice that operates across multiple states including California, Florida, Georgia, Hawaii, Indiana, Maine, New York, Texas, and Virginia.",
    },
    {
      question: "Has Shariq Refai worked with professional athletes?",
      answer:
        "Yes. Dr. Refai's professional experience in sports and performance psychiatry includes affiliations involving the NFL Substance Abuse Program and the Jacksonville Jaguars. He holds board certification from the American Board of Sports and Performance Psychiatry.",
    },
    {
      question: "Does shariqrefai.com offer clinical care or accept patients?",
      answer:
        "No. shariqrefai.com is an educational author and media platform. It does not offer clinical care, treatment, diagnosis, or a physician-patient relationship. For clinical care inquiries, please visit shrinkmd.com or contact a qualified, licensed clinician of your choosing.",
    },
  ]);

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        url="/about"
        image="/og-about.jpg"
        keywords="Shariq Refai biography, board-certified psychiatrist, Jacksonville psychiatrist, shrinkMD founder, sports psychiatry, mental health educator"
        schema={[
          personSchema,
          aboutPage,
          aboutFaq,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />

      {/* Hero / H1 */}
      <section className="pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-6xl">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              { name: "About" },
            ]}
          />
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            <div className="md:col-span-7 order-2 md:order-1">
              {/* Credibility eyebrow (Master Brief hero anatomy) */}
              <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
                About · Shariq Refai, MD, MBA, FAPA · Board-Certified Psychiatrist
              </p>
              {/* Outcome-led H1 (Master Brief About-page rule: transformation first) */}
              <h1 className="mt-4 text-3xl md:text-5xl font-serif text-primary leading-[1.15] tracking-tight">
                A psychiatrist building the mental-health infrastructure the field is missing.
              </h1>
              {/* Subhead: offer + mechanism + audience */}
              <p className="mt-6 text-lg md:text-xl text-secondary font-light leading-relaxed">
                Board-certified psychiatrist. Founder of shrinkMD and The Shrink Network. Author of three books on overthinking and anxiety. Medical reviewer for six network publications. Based in Jacksonville, Florida.
              </p>
              {/* Primary + secondary CTA */}
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  href="/perspectives"
                  className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
                >
                  Read a perspective
                </Link>
                <Link
                  href="/media-inquiries"
                  className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
                >
                  Media and speaking
                </Link>
              </div>
              {/* Proof strip (proof near the claim) */}
              <ul aria-label="Credentials" className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground list-none p-0">
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Board certified (ABPN)</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>FAPA</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>Duke MBA</li>
                <li aria-hidden="true" className="text-border">·</li>
                <li className="flex items-center gap-2"><span aria-hidden="true" className="text-accent">✓</span>15+ years in practice</li>
              </ul>
              <p className="mt-6 text-xs text-muted-foreground font-light">
                Authored and editorially reviewed by Shariq Refai, MD, MBA · last reviewed{" "}
                <time dateTime="2026-05-15">May 15, 2026</time>
              </p>
              <p className="mt-4 text-xs text-muted-foreground font-light leading-relaxed max-w-2xl">
                This page is a professional biography. It's educational, not medical advice, and doesn't establish a physician-patient relationship.
              </p>
            </div>
            <div className="md:col-span-5 order-1 md:order-2">
              <figure className="relative m-0">
                <img
                  src={COAST_SRC}
                  alt={COAST_ALT}
                  width={1200}
                  height={900}
                  className="w-full h-auto object-cover aspect-[4/3] shadow-sm"
                  loading="eager"
                  fetchPriority="high"
                />
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* At a Glance */}
      <section className="at-a-glance py-16 md:py-20 px-4 md:px-8 bg-background">
        <motion.div
          className="container mx-auto max-w-4xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeInUp}
        >
          <div className="mb-6">
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              At a Glance
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mb-6">
            Who He Is
          </h2>
          <p className="text-lg md:text-xl text-secondary font-light leading-relaxed">
            Shariq Refai, MD, MBA, is a board-certified psychiatrist, founder
            of shrinkMD and shrinQ, creator of the Unstuck app, author, and mental health
            educator based in Jacksonville, Florida. He holds dual board
            certifications in psychiatry and sports/performance psychiatry, an
            MBA from Duke, and certificates in Electroconvulsive Therapy and
            Obesity Medicine from Columbia University.
          </p>
        </motion.div>
      </section>

      {/* Main grid: portrait + credentials sidebar, story body */}
      <section className="py-16 md:py-24 px-4 md:px-8 container mx-auto max-w-5xl">
        <motion.div
          className="grid md:grid-cols-[1fr_2fr] gap-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={fadeInUp}
        >
          {/* Sidebar */}
          <div className="flex flex-col gap-10">
            <img
              src={headshot}
              alt="Shariq Refai, MD, MBA, board-certified psychiatrist, portrait."
              className="w-full grayscale-[10%]" width={800} height={800} loading="lazy" decoding="async"/>

            <div className="credentials-section space-y-4">
              <h2 className="font-serif text-xl text-primary border-b border-border pb-2">
                Credentials
              </h2>
              <ul className="text-sm space-y-2 text-secondary font-light leading-relaxed">
                <li>
                  Board-Certified,{" "}
                  <a
                    href="https://www.abpn.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    American Board of Psychiatry and Neurology
                  </a>
                </li>
                <li>Board-Certified, American Board of Sports and Performance Psychiatry</li>
                <li>
                  Fellow,{" "}
                  <a
                    href="https://www.psychiatry.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    American Psychiatric Association
                  </a>{" "}
                  (FAPA)
                </li>
                <li>
                  <a
                    href="https://www.fuqua.duke.edu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    Duke University
                  </a>
                  , MBA
                </li>
                <li>
                  <a
                    href="https://www.sgu.edu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    St. George's University School of Medicine
                  </a>
                  , MD
                </li>
                <li>
                  <a
                    href="https://www.unf.edu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    University of North Florida
                  </a>
                  , BS
                </li>
                <li>
                  Certificate in Electroconvulsive Therapy,{" "}
                  <a
                    href="https://www.columbia.edu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    Columbia University
                  </a>
                </li>
                <li>Certificate in Obesity Medicine, Columbia University</li>
                <li>
                  Member,{" "}
                  <a
                    href="https://councils.forbes.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-accent transition-colors"
                  >
                    Forbes Business Council
                  </a>
                </li>
              </ul>
            </div>

            <div className="space-y-4">
              <h2 className="font-serif text-xl text-primary border-b border-border pb-2">
                Professional Profiles
              </h2>
              <ul className="text-sm space-y-2 font-light">
                <li>
                  <a
                    href="https://www.linkedin.com/in/shariqrefai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:text-accent transition-colors"
                  >
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.doximity.com/pub/shariq-refai-md"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:text-accent transition-colors"
                  >
                    Doximity
                  </a>
                </li>
                <li>
                  <a
                    href={withNetworkUtm("https://shrinkmd.com/shariq-refai", "about_page")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:text-accent transition-colors"
                  >
                    shrinkMD Team Profile
                  </a>
                </li>
                <li>
                  <a
                    href="https://councils.forbes.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-secondary hover:text-accent transition-colors"
                  >
                    Forbes Business Council
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Body */}
          <div className="prose prose-lg prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-p:font-light prose-p:text-secondary prose-strong:text-primary prose-a:text-accent prose-a:no-underline hover:prose-a:underline max-w-none">
            <h2>How I Got Here</h2>
            <p>
              I didn't plan on psychiatry. In college at the University of
              North Florida, I worked nights as a psychiatric tech. My job was
              simple on paper. Sit with people who were hurting.
            </p>
            <p>
              Some nights it was a business owner who couldn't get out of bed.
              Other nights it was a man who'd lost almost everything he'd
              built. The lesson landed in me early and stayed. Mental health
              doesn't care about bank accounts or zip codes. It can show up in
              anyone, anywhere. What I saw in those rooms wasn't a character
              flaw. It was people caught inside systems and stories that told
              them they were the problem.
            </p>
            <p>
              I went to St. George's University School of Medicine after that.
              Residency took me to the University of Hawaii and to John Peter
              Smith Hospital in Texas. I picked up certificates in
              Electroconvulsive Therapy and Obesity Medicine from Columbia
              along the way, and finished a Duke MBA because I wanted to
              understand how systems get built. Fifteen years later, I'm
              still answering the same question I started with. What
              actually helps when someone is suffering, and how do we build
              a more humane public conversation about it.
            </p>

            <h2>How I Think About the Mind</h2>
            <p>
              Three principles run through my editorial and educational
              work.
            </p>
            <p>
              <strong>The body moves first.</strong> By the time most people
              notice they're anxious, their nervous system has already shifted.
              Heart up, breath high, muscles tight. Trying to think your way
              out of an activated body is like trying to talk yourself warm.
              The body needs a real input first. The thinking comes later.
            </p>
            <p>
              <strong>A thought isn't the same as thinking.</strong> Thoughts
              arrive without permission. Thinking is what you do with them, the
              replaying, the rehearsing, the building of a story around a
              sentence your brain handed you in passing. Most suffering lives
              in the thinking, not the thought. The work isn't to control what
              arrives. It's to stop following every arrival into a six-hour
              spiral.
            </p>
            <p>
              <strong>Most advice fails in the moment.</strong> "Just breathe."
              "Challenge your thoughts." "Reframe it." These aren't wrong.
              They're sequenced wrong. The cognitive tools work when the body
              is calm enough to use them. In the middle of a surge, the body
              needs regulation first. Skip that step and the tools backfire.
            </p>

            <h2>Topics I Write and Speak About</h2>
            <p>
              My areas of professional expertise span anxiety,
              overthinking, depression, obsessive-compulsive patterns, panic,
              trauma responses, bipolar patterns, and the high-functioning
              presentations most people quietly carry. I have specialized
              training in sports and performance psychiatry through the
              American Board of Sports and Performance Psychiatry, and my
              professional experience in sports and performance psychiatry
              includes affiliations involving the NFL Substance Abuse Program
              and the Jacksonville Jaguars.
            </p>
            <p>
              The patterns I write about most often are the high-functioning
              ones. The executive who can't slow down. The parent who can't
              sleep. The athlete whose body keeps performing while her mind
              runs a race that never ends. The student who's reading the
              books, doing the work, and still can't quiet the noise. None of
              them are failing. All of them are running a nervous system that
              learned a lesson too well.
            </p>
            <p>
              Named positions on how the profession itself is trained, paid,
              and practiced live in my{" "}
              <a href={withNetworkUtm("https://shrinkiatry.com/opinion/", "about_topics")} target="_blank" rel="noopener noreferrer">
                labeled opinion column at shrinkiatry
              </a>
              . That's the editorial voice on psychiatry as a system, kept
              separate from the educational essays here.
            </p>
            <p className="text-sm">
              <em>
                shariqrefai.com is an educational author and media platform.
                It does not provide clinical care, treatment, diagnosis, or a
                physician-patient relationship.
              </em>
            </p>

            <h2>Founder &amp; Executive Focus</h2>
            <p>
              Dr. Refai is also the founder of the{" "}
              <a href={withNetworkUtm("https://shrinknetwork.com/", "about_page")} rel="noopener">
                Shrink Network
              </a>
              , an umbrella of independent editorial mental health publications
              including AnxietyResource.org, DepressionResource.org,
              AnxietyResearch.org, PsychiatryRx.org, Shrinkopedia,
              Shrinktionary, and ShrinkDaily. He serves as the medical editor
              for each publication in the network. The network's profession
              layer lives at{" "}
              <a href={withNetworkUtm("https://shrinkiatry.com/", "about_founder_focus")} target="_blank" rel="noopener noreferrer">
                shrinkiatry
              </a>
              , where{" "}
              <a href={withNetworkUtm("https://shrinkiatry.com/psychiatry-operating-room/", "about_founder_focus")} target="_blank" rel="noopener noreferrer">
                The Psychiatry Operating Room
              </a>{" "}
              maps how the field is trained, paid, and practiced.
            </p>
            <p>
              I built{" "}
              <a href={withNetworkUtm("https://shrinkmd.com/", "about_page")} target="_blank" rel="noopener noreferrer">
                shrinkMD
              </a>{" "}
              because the access gap in psychiatry is real and most existing
              digital options weren't closing it in a way I'd recommend to
              my own family. shrinkMD is an independent multistate
              telepsychiatry company built around continuity of clinician,
              real follow-up, and multistate licensure. shrinkMD is a
              separate clinical entity from shariqrefai.com. Clinical care
              inquiries should be directed to shrinkmd.com. For the team,
              licenses, and how the practice is structured, see the{" "}
              <a href={withNetworkUtm("https://shrinkmd.com/about", "about_page_practice")} target="_blank" rel="noopener noreferrer">
                shrinkMD (my practice) About page
              </a>
              .
            </p>
            <p>
              <a href={withNetworkUtm("https://beunstuck.app/", "about_page")} target="_blank" rel="noopener noreferrer">
                Unstuck
              </a>{" "}
              is the wellness side. It's a mental wellness app for reflection,
              journaling, reminders, and emotional awareness. It's not a
              medical device. It's not therapy. It's the daily practice tool
              that sits alongside care, not a substitute for it. The honest
              separation between wellness and clinical work is something I
              take seriously, and Unstuck reflects that.
            </p>
            <p>
              shrinkMD Publishing is the imprint behind the books. Educational
              frameworks for the general public, written by a clinician, with
              the disclaimers and the science the topic actually deserves.
            </p>

            <h2>Upcoming Publications</h2>
            <p>
              Three books are on the way. <em>Your Mind Is Full of Sh*t</em> is
              the main one, a psychiatrist's guide to overthinking and anxiety
              written for the person who's already tried the apps. The
              companion workbook is a hands-on follow-along.{" "}
              <em>The Havoc in Your Head</em> is the in-the-moment response
              framework, what to do when your mind takes over.
            </p>
            <p>
              Visit the <Link href="/books">Books page</Link> for descriptions,
              status, and pre-order updates.
            </p>

            <h2>Verified Profiles</h2>
            <p>
              Authoritative identifiers and profiles you can use to verify
              authorship and credentials:
            </p>
            <ul className="verified-profiles">
              <li>
                <a
                  href="https://orcid.org/0009-0009-1090-4373"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="/orcid-id-icon.svg"
                    alt=""
                    width={16}
                    height={16}
                    aria-hidden="true"
                    style={{ display: "inline-block", verticalAlign: "-2px", marginRight: "0.4em" }}
                  />
                  ORCID: 0009-0009-1090-4373
                </a>
              </li>
              <li>
                <a
                  href="https://www.wikidata.org/wiki/Q139822307"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Wikidata: Q139822307
                </a>
              </li>
              <li>
                <a
                  href="https://npiregistry.cms.hhs.gov/provider-view/1467680660"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NPI: 1467680660
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/in/shariqrefai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href="https://www.doximity.com/pub/shariq-refai-md"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Doximity
                </a>
              </li>
              <li>
                <a
                  href="https://councils.forbes.com/profile/Shariq-Refai-MD-MBA-Board-Certified-Psychiatrist-%7C-Founder-CEO-shrinkMD-shrinkMD/c1649312-6792-4cd3-b764-df4c393ec6c7"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Forbes Business Council
                </a>
              </li>
            </ul>

            <h2>Featured In</h2>
            <p>
              Dr. Refai has been featured as an expert source in{" "}
              {PRESS_FEATURES.map((feature, index) => (
                <span key={feature.url}>
                  <a href={feature.url} target="_blank" rel="noopener noreferrer">
                    {feature.publisher}
                  </a>
                  {index < PRESS_FEATURES.length - 2
                    ? ", "
                    : index === PRESS_FEATURES.length - 2
                      ? ", and "
                      : "."}
                </span>
              ))}
            </p>

            <h2>Editorial Reviewer Role</h2>
            <p>
              Beyond my work at shariqrefai.com, I serve as the medical editor
              and clinical reviewer for five independent editorial publications:
            </p>
            <ul>
              <li>
                <a
                  href={withNetworkUtm("https://shrinkopedia.com/authors/shariq-refai/", "about_editorial_role")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>Shrinkopedia</strong>
                </a>
                , an evidence-based mental health encyclopedia. My author
                profile at Shrinkopedia carries every entry I've reviewed,
                and Shrinkopedia is the network's reference-layer counterpart
                to Shrinktionary's definitions.
              </li>
              <li>
                <a
                  href={withNetworkUtm("https://anxietyresource.org/", "about_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>AnxietyResource.org</strong>
                </a>
                , an independent editorial publication on anxiety with a
                50-entry plain-language Glossary and complete state-by-state
                directories.
              </li>
              <li>
                <a
                  href={withNetworkUtm("https://depressionresource.org/", "about_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>DepressionResource.org</strong>
                </a>
                , a long-form patient education site on depression including
                screening tools, safety plan templates, and crisis resources.
              </li>
              <li>
                <a
                  href={withNetworkUtm("https://anxietyresearch.org/", "about_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>AnxietyResearch.org</strong>
                </a>
                , an editorial publication translating current anxiety research
                into plain-language summaries for general readers.
              </li>
              <li>
                <a
                  href={withNetworkUtm("https://psychiatryrx.org/", "about_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <strong>PsychiatryRx.org</strong>
                </a>
                , a plain-language reference site publishing psychiatrist-reviewed
                guides to psychiatric and sleep medications, sourced from FDA
                labeling and clinical guidelines.
              </li>
            </ul>
            <p>
              Every article on all four sites carries my "Authored and editorially
              reviewed by" credit with a publish and last-reviewed date. My
              role on all four sites is editorial review for clinical accuracy.
              All four sites are editorially independent. None sells
              anything, runs ads, or takes affiliate commissions. See the{" "}
              <a href="/medical-review-board">Medical Review Board page</a>{" "}
              for reviewer roles and standards.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Media & Speaking band */}
      <section className="bg-secondary text-secondary-foreground py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-5xl grid md:grid-cols-[2fr_1fr] gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif mb-6 text-white">
              Media &amp; Speaking
            </h2>
            <p className="text-secondary-foreground/80 font-light leading-relaxed mb-4">
              I speak on the modern anxiety loop, overthinking and the nervous
              system, mental health literacy in the workplace, performance
              pressure and emotional health, and the responsible future of
              digital mental health. Audiences include corporate teams,
              professional associations, athletic organizations, and
              conferences.
            </p>
            <p className="text-secondary-foreground/80 font-light leading-relaxed mb-8">
              For media inquiries, podcast appearances, or speaking
              engagements, the <Link href="/media" className="text-white underline decoration-white/40 underline-offset-4 hover:decoration-white">Media page</Link> has
              the press kit, and the <Link href="/media-inquiries" className="text-white underline decoration-white/40 underline-offset-4 hover:decoration-white">Media &amp; Press Inquiries page</Link> has
              the dedicated inquiry form.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                href="/speaking"
                className="inline-flex h-12 items-center justify-center border border-white/30 text-white px-8 text-sm font-medium hover:bg-white hover:text-secondary transition-colors"
              >
                View Speaking Topics
              </Link>
              <Link
                href="/media"
                className="inline-flex h-12 items-center justify-center bg-white text-secondary px-8 text-sm font-medium hover:bg-accent hover:text-white transition-colors"
              >
                Press Resources
              </Link>
            </div>
          </div>
          <div>
            <img
              src={speakingPhoto}
              alt="Shariq Refai, MD, MBA, speaking to an audience."
              className="w-full grayscale opacity-80 mix-blend-luminosity" width={1000} height={1500} loading="lazy" decoding="async"/>
          </div>
        </div>
      </section>

      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="about_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>

      {/* IndieAuth / relmeauth verified profile links. Hidden visually but
          discoverable by identity crawlers and IndieAuth agents. */}
      <div className="sr-only" aria-hidden="false">
        <a rel="me" href="https://www.linkedin.com/in/shariqrefai">LinkedIn</a>
        <a rel="me" href="https://www.doximity.com/pub/shariq-refai-md">Doximity</a>
        <a rel="me" href="https://orcid.org/0009-0009-1090-4373">ORCID</a>
        <a rel="me" href="https://www.wikidata.org/wiki/Q139822307">Wikidata</a>
      </div>
        </SiteLayout>
  );
}
